import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import { Button, Col, Form, FormGroup, Row, Spinner } from "react-bootstrap";
import * as yup from "yup";
import api from "common/api";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ISubject, SubjectKind } from "common/types";
import axios from "axios";
import TeacherPictureUpload from "./TeacherPictureUpload";
import usePageTitle from "common/usePageTitle";
import { useAuth } from "authContext";
import * as Sentry from "@sentry/react";

interface TeacherAddEditFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  title: string;
  experience: string;
  hidden: boolean;
  remark: string;
  subjects: number[];
}

interface TeacherAddEditPageProps {
  kind: SubjectKind;
}

function TeacherAddEditPage({ kind }: TeacherAddEditPageProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { userProfile } = useAuth();

  const [subjects, setSubjects] = useState<ISubject[]>([]);
  const [initialValues, setInitialValues] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    title: "",
    experience: "",
    hidden: false,
    remark: "",
    picture: null,
    subjects: [] as number[],
  });

  const isEditMode = id != null;

  const getProfileKey = () =>
    kind === SubjectKind.LESSON ? "lesson_profile" : "profile";

  usePageTitle(isEditMode ? "Ažuriraj nastavnika" : "Dodaj novog nastavnika");

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await api.get("/subjects/");
        const filteredSubjects = response.data.filter(
          (subject: ISubject) => subject.kind === kind,
        );
        setSubjects(filteredSubjects);
      } catch (error) {
        console.error(error);
      }
    };

    const fetchInstructorData = async () => {
      if (isEditMode) {
        try {
          const response = await api.get(`/teachers/${id}/`);
          const teacherData = response.data;
          const profileKey = getProfileKey();
          const profileData = teacherData[profileKey];

          if (profileData == null) {
            console.error(`No ${profileKey} data found for this teacher`);
            toast.error("Nije moguće učitati podatke nastavnika");
            return;
          }

          setInitialValues({
            first_name: teacherData.first_name,
            last_name: teacherData.last_name,
            email: teacherData.email,
            phone_number: profileData.phone_number,
            title: profileData.title,
            experience: profileData.experience,
            hidden: profileData.hidden,
            remark: profileData.remark,
            picture: profileData.picture,
            subjects: profileData.subjects.map((s: ISubject) => Number(s.id)),
          });
        } catch (error) {
          console.error(error);
          toast.error("Greška prilikom učitavanja podataka nastavnika");
        }
      }
    };

    fetchSubjects();
    fetchInstructorData();
  }, [id, isEditMode, userProfile?.role]);

  const schema = yup.object({
    first_name: yup.string().required("Potrebno je unijeti ime"),
    last_name: yup.string().required("Potrebno je unijeti prezime"),
    email: isEditMode
      ? yup.string()
      : yup
          .string()
          .email("Unesite valjanu email adresu")
          .required("Potrebno je unijeti email"),
    phone_number: yup.string().required("Potrebno je unijeti broj telefona"),
    title:
      kind === SubjectKind.LESSON
        ? yup.string()
        : yup.string().required("Potrebno je unijeti titulu"),
    experience:
      kind === SubjectKind.LESSON
        ? yup.string()
        : yup.string().required("Potrebno je unijeti iskustvo"),
    remark: yup.string().required("Potrebno je unijeti napomenu"),
    subjects: yup.array(),
  });

  const handleSubmit = async (formData: TeacherAddEditFormData) => {
    const profileData = {
      phone_number: formData.phone_number,
      title: formData.title,
      experience: formData.experience,
      remark: formData.remark,
      subjects: formData.subjects,
      hidden: formData.hidden, // Include hidden field in profile data
    };

    const requestData: any = {
      first_name: formData.first_name,
      last_name: formData.last_name,
    };

    requestData[getProfileKey()] = profileData;

    if (!isEditMode) {
      requestData.email = formData.email;
    }

    try {
      if (isEditMode) {
        await api.patch(`/teachers/${id}/`, requestData);
        toast.success("Nastavnik je ažuriran.");
      } else {
        await api.post("/teachers/", requestData);
        toast.success("Nastavnik dodan.");
      }
      if (kind === SubjectKind.LESSON) {
        navigate("/poduke/nastavnici-admin/");
      } else if (kind === SubjectKind.PREPARATION) {
        navigate("/nastavnici-admin");
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data;
        if (typeof errorData === "object" && errorData !== null) {
          if (
            Array.isArray(errorData.email) &&
            errorData.email[0] ===
              "user with this email address already exists."
          ) {
            toast.error("Korisnik s ovom email adresom već postoji.");
          } else {
            toast.error("Greška prilikom dodavanja/ažuriranja nastavnika.");
            Sentry.captureException(error);
          }
        }
      } else {
        // Handle non-Axios errors
        toast.error("Greška prilikom dodavanja/ažuriranja nastavnika.");
        Sentry.captureException(error);
      }
    }
  };

  if (isEditMode && subjects.length === 0) {
    return <Spinner />;
  }

  return (
    <div>
      <h1>{isEditMode ? "Ažuriraj nastavnika" : "Dodaj novog nastavnika"}</h1>

      <Formik
        validationSchema={schema}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        enableReinitialize
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          errors,
          setFieldValue,
          isSubmitting,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Row className="mb-3">
              <FormGroup as={Col} controlId="formFirstName">
                <Form.Label>Ime</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Unesite ime"
                  name="first_name"
                  value={values.first_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.first_name && !!errors.first_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.first_name}
                </Form.Control.Feedback>
              </FormGroup>

              <FormGroup as={Col} controlId="formLastName">
                <Form.Label>Prezime</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Unesite prezime"
                  name="last_name"
                  value={values.last_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.last_name && !!errors.last_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.last_name}
                </Form.Control.Feedback>
              </FormGroup>
            </Row>

            <Row className="mb-3">
              <FormGroup as={Col} controlId="formEmail">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder={isEditMode ? "" : "Unesite email"}
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.email && !!errors.email}
                  disabled={isEditMode}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </FormGroup>

              <FormGroup as={Col} controlId="formPhoneNumber">
                <Form.Label>Broj telefona</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Unesite broj telefona"
                  name="phone_number"
                  value={values.phone_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.phone_number && !!errors.phone_number}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phone_number}
                </Form.Control.Feedback>
              </FormGroup>
            </Row>

            {kind !== SubjectKind.LESSON && (
              <>
                <FormGroup as={Col} controlId="formTitle" className="mb-3">
                  <Form.Label>Titula</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Unesite titulu"
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.title && !!errors.title}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.title}
                  </Form.Control.Feedback>
                </FormGroup>

                <FormGroup as={Col} controlId="formExperience" className="mb-3">
                  <Form.Label>Iskustvo</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Opis iskustva"
                    name="experience"
                    value={values.experience}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.experience && !!errors.experience}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.experience}
                  </Form.Control.Feedback>
                </FormGroup>
              </>
            )}

            <FormGroup as={Col} controlId="formRemark" className="mb-3">
              <Form.Label>Napomena</Form.Label>
              <Form.Control
                type="text"
                placeholder="Unesite napomenu"
                name="remark"
                value={values.remark}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.remark && !!errors.remark}
              />
              <Form.Control.Feedback type="invalid">
                {errors.remark}
              </Form.Control.Feedback>
            </FormGroup>

            <FormGroup controlId="formHidden" className="mb-3">
              <Form.Check
                type="checkbox"
                id="hidden"
                label="Sakriveni profil"
                name="hidden"
                checked={values.hidden}
                onChange={handleChange}
              />
            </FormGroup>

            {isEditMode && (
              <TeacherPictureUpload
                userId={id}
                initialImageUrl={values.picture}
              />
            )}

            <FormGroup controlId="formSubjects" className="mb-3">
              <Form.Label>Predmeti</Form.Label>
              <Row>
                {subjects.map((subject) => (
                  <Col key={subject.id} xs={12} sm={6}>
                    <Form.Check
                      type="checkbox"
                      id={subject.id.toString()}
                      value={subject.id}
                      label={subject.name}
                      name="subjects"
                      onChange={(e) => {
                        const subjId = Number(e.target.value);
                        if (e.target.checked) {
                          setFieldValue("subjects", [
                            ...values.subjects,
                            subjId,
                          ]);
                        } else {
                          setFieldValue(
                            "subjects",
                            values.subjects.filter((id) => id !== subjId),
                          );
                        }
                      }}
                      checked={values.subjects.includes(Number(subject.id))}
                    />
                  </Col>
                ))}
              </Row>
              <div
                className={
                  "invalid-feedback " + (!!errors.subjects ? "d-block" : "")
                }
              >
                {errors.subjects}
              </div>
            </FormGroup>

            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting}
              className="mb-4 float-end"
            >
              {isSubmitting ? (
                <Spinner as="span" size="sm" role="status" aria-hidden="true" />
              ) : null}
              {isEditMode ? "Ažuriraj nastavnika" : "Dodaj nastavnika"}
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default TeacherAddEditPage;
